import { useState } from 'react'
import { useNavigate, Link } from 'react-router-dom'
import { Button, Card, CardContent, CardHeader, CardTitle, Input } from '@agms/ui'
import { useCreateLead, LEAD_SOURCES, OWNERSHIP_TYPES } from '../hooks/useLeads'
import { usePermissions } from '../hooks/useAuth'
import { apiErrorCode, apiErrorStatus, apiValidationErrors } from '../hooks/useErrorHandler'
import type { CreateLeadRequest } from '../lib/api'

/**
 * New lead creation form.
 * Creates a lead in "new_business" stage with "needs_analysis" sub-stage.
 */
export function NewLeadPage() {
  const navigate = useNavigate()
  const { canCreateLeads } = usePermissions()
  const createLead = useCreateLead()

  const [validationErrors, setValidationErrors] = useState<Record<string, string[]>>({})

  // Handle form submit
  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault()
    setValidationErrors({})

    const formData = new FormData(e.currentTarget)
    const data: CreateLeadRequest = {}

    // String fields
    const stringFields = [
      'dba_name', 'legal_name', 'contact_name', 'contact_phone', 'business_email',
      'website', 'business_description', 'ownership_type', 'lead_source', 'notes'
    ] as const
    stringFields.forEach(field => {
      const value = formData.get(field)
      if (value) data[field] = value as string
    })

    // Numeric fields
    const numericFields = [
      'years_in_business', 'monthly_volume', 'avg_ticket', 'high_ticket',
      'pct_card_swiped', 'pct_ecommerce', 'pct_cnp'
    ] as const
    numericFields.forEach(field => {
      const value = formData.get(field)
      if (value) data[field] = parseFloat(value as string)
    })

    // Boolean fields
    data.has_tax_id = formData.get('has_tax_id') === 'on'
    data.has_checking_account = formData.get('has_checking_account') === 'on'

    // Validate required fields
    if (!data.dba_name && !data.legal_name) {
      setValidationErrors({ dba_name: ['Either DBA Name or Legal Name is required'] })
      return
    }

    createLead.mutate(data, {
      onSuccess: (lead) => {
        navigate(`/leads/${lead.id}`)
      },
      onError: (error) => {
        // Real validation failure — surface the per-field errors.
        const errors = apiValidationErrors(error)
        if (apiErrorStatus(error) === 422 && errors) {
          setValidationErrors(errors)
          return
        }
        // Timeout or 504 from the ALB — the server may have actually
        // created the lead but the client gave up waiting. Send the
        // user to the leads list so they can find their record rather
        // than stranding them on a "failed" form with a saved lead.
        const status = apiErrorStatus(error)
        const isTimeout = apiErrorCode(error) === 'ECONNABORTED' || status === 504 || status === 408
        if (isTimeout) {
          navigate('/leads')
        }
      },
    })
  }

  if (!canCreateLeads()) {
    return (
      <div className="space-y-6">
        <div className="flex items-center justify-center py-12">
          <div className="text-center">
            <div className="text-red-500 mb-4">You do not have permission to create leads.</div>
            <Link to="/leads">
              <Button variant="secondary">Back to Leads</Button>
            </Link>
          </div>
        </div>
      </div>
    )
  }

  return (
    <div className="space-y-6">
      {/* Page Header */}
      <div className="flex items-center gap-4">
        <Link to="/leads" className="text-gray-500 hover:text-gray-700">
          <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 19l-7-7m0 0l7-7m-7 7h18" />
          </svg>
        </Link>
        <div>
          <h1 className="text-2xl font-bold text-gray-900">New Lead</h1>
          <p className="mt-1 text-sm text-gray-500">
            Create a new lead to start the pipeline process
          </p>
        </div>
      </div>

      {/* Form */}
      <form onSubmit={handleSubmit}>
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Main Form */}
          <div className="lg:col-span-2 space-y-6">
            {/* Business Information */}
            <Card>
              <CardHeader>
                <CardTitle>Business Information</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <Input
                      name="dba_name"
                      label="DBA Name"
                      placeholder="Doing business as..."
                      error={validationErrors.dba_name?.[0]}
                    />
                    <p className="mt-1 text-xs text-gray-500">The name the business operates under</p>
                  </div>
                  <div>
                    <Input
                      name="legal_name"
                      label="Legal Name"
                      placeholder="Registered legal name..."
                      error={validationErrors.legal_name?.[0]}
                    />
                    <p className="mt-1 text-xs text-gray-500">As registered with the state</p>
                  </div>
                  <Input
                    name="website"
                    label="Website"
                    placeholder="https://..."
                    error={validationErrors.website?.[0]}
                  />
                  <div>
                    <label htmlFor="ownership_type" className="block text-sm font-medium text-gray-700 mb-1">Ownership Type</label>
                    <select
                      id="ownership_type"
                      name="ownership_type"
                      className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
                    >
                      <option value="">Select...</option>
                      {OWNERSHIP_TYPES.map(type => (
                        <option key={type} value={type}>{type}</option>
                      ))}
                    </select>
                  </div>
                </div>
                <div>
                  <label htmlFor="business_description" className="block text-sm font-medium text-gray-700 mb-1">Business Description</label>
                  <textarea
                    id="business_description"
                    name="business_description"
                    rows={3}
                    className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
                    placeholder="What does this business do? Products/services offered..."
                  />
                </div>
              </CardContent>
            </Card>

            {/* Contact Information */}
            <Card>
              <CardHeader>
                <CardTitle>Contact Information</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <Input
                    name="contact_name"
                    label="Contact Name"
                    placeholder="Primary contact..."
                    error={validationErrors.contact_name?.[0]}
                  />
                  <Input
                    name="contact_phone"
                    label="Phone"
                    placeholder="(555) 555-5555"
                    error={validationErrors.contact_phone?.[0]}
                  />
                  <Input
                    name="business_email"
                    label="Email"
                    type="email"
                    placeholder="email@business.com"
                    error={validationErrors.business_email?.[0]}
                  />
                </div>
              </CardContent>
            </Card>

            {/* Business Profile */}
            <Card>
              <CardHeader>
                <CardTitle>Business Profile</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                  <Input
                    name="years_in_business"
                    label="Years in Business"
                    type="number"
                    min="0"
                    placeholder="0"
                    error={validationErrors.years_in_business?.[0]}
                  />
                  <Input
                    name="monthly_volume"
                    label="Monthly Volume ($)"
                    type="number"
                    min="0"
                    placeholder="0"
                    error={validationErrors.monthly_volume?.[0]}
                  />
                  <Input
                    name="avg_ticket"
                    label="Average Ticket ($)"
                    type="number"
                    min="0"
                    placeholder="0"
                    error={validationErrors.avg_ticket?.[0]}
                  />
                  <Input
                    name="high_ticket"
                    label="High Ticket ($)"
                    type="number"
                    min="0"
                    placeholder="0"
                    error={validationErrors.high_ticket?.[0]}
                  />
                </div>

                <div>
                  <h4 className="text-sm font-medium text-gray-700 mb-3">Processing Mix (%)</h4>
                  <div className="grid grid-cols-3 gap-4">
                    <Input
                      name="pct_card_swiped"
                      label="Card Swiped"
                      type="number"
                      min="0"
                      max="100"
                      placeholder="0"
                      error={validationErrors.pct_card_swiped?.[0]}
                    />
                    <Input
                      name="pct_ecommerce"
                      label="E-commerce"
                      type="number"
                      min="0"
                      max="100"
                      placeholder="0"
                      error={validationErrors.pct_ecommerce?.[0]}
                    />
                    <Input
                      name="pct_cnp"
                      label="Card Not Present"
                      type="number"
                      min="0"
                      max="100"
                      placeholder="0"
                      error={validationErrors.pct_cnp?.[0]}
                    />
                  </div>
                  <p className="mt-2 text-xs text-gray-500">
                    Processing mix should total 100% across all categories
                  </p>
                </div>
              </CardContent>
            </Card>

            {/* Notes */}
            <Card>
              <CardHeader>
                <CardTitle>Notes</CardTitle>
              </CardHeader>
              <CardContent>
                <textarea
                  aria-label="Lead notes"
                  name="notes"
                  rows={4}
                  className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
                  placeholder="Any additional notes about this lead..."
                />
              </CardContent>
            </Card>
          </div>

          {/* Sidebar */}
          <div className="space-y-6">
            {/* Lead Source */}
            <Card>
              <CardHeader>
                <CardTitle>Lead Source</CardTitle>
              </CardHeader>
              <CardContent>
                <select
                  aria-label="Lead source"
                  name="lead_source"
                  className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
                >
                  <option value="">Select source...</option>
                  {Object.entries(LEAD_SOURCES).map(([key, label]) => (
                    <option key={key} value={key}>{label}</option>
                  ))}
                </select>
                <p className="mt-2 text-xs text-gray-500">
                  How did this lead come to AGMS?
                </p>
              </CardContent>
            </Card>

            {/* Qualification */}
            <Card>
              <CardHeader>
                <CardTitle>Qualification</CardTitle>
              </CardHeader>
              <CardContent className="space-y-3">
                <label className="flex items-center gap-3 p-3 rounded-md border border-gray-200 hover:bg-gray-50 cursor-pointer">
                  <input
                    type="checkbox"
                    name="has_tax_id"
                    className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                  />
                  <div>
                    <span className="text-sm font-medium text-gray-700">Has Tax ID</span>
                    <p className="text-xs text-gray-500">SSN, EIN, or ITIN</p>
                  </div>
                </label>
                <label className="flex items-center gap-3 p-3 rounded-md border border-gray-200 hover:bg-gray-50 cursor-pointer">
                  <input
                    type="checkbox"
                    name="has_checking_account"
                    className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                  />
                  <div>
                    <span className="text-sm font-medium text-gray-700">Has Checking Account</span>
                    <p className="text-xs text-gray-500">Open US checking account</p>
                  </div>
                </label>
                <p className="text-xs text-gray-500 mt-2">
                  Both are required for merchant onboarding
                </p>
              </CardContent>
            </Card>

            {/* Submit */}
            <Card>
              <CardContent className="py-4">
                <Button type="submit" className="w-full" isLoading={createLead.isPending}>
                  Create Lead
                </Button>
                <p className="mt-2 text-xs text-gray-500 text-center">
                  Lead will be created in "New Business" stage
                </p>
              </CardContent>
            </Card>
          </div>
        </div>
      </form>
    </div>
  )
}

export default NewLeadPage
